import { Bell, CalendarClock, CircleAlert, CircleCheck, ReceiptText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/shared/lib';
import { Button, EmptyState } from '@doccareplus/ui';
import { toastApiError } from '@doccareplus/kernel/application';
import { useMarkAllNotificationsReadCommand, useMarkNotificationReadCommand } from '../application/notifications.command';
import { useNotificationsInfiniteQuery } from '../application/notifications.query';
import { getNotificationRelativeTime, typeToTone } from '../domain/notificationView';
import type { NotificationItem } from '../infrastructure/notifications.api';
import { NotificationsSkeleton } from './NotificationsSkeleton';

export function NotificationList({
  onNotificationSelected,
  showMarkAll = false,
}: {
  onNotificationSelected?: () => void;
  showMarkAll?: boolean;
}) {
  const { t } = useTranslation('notifications');
  const query = useNotificationsInfiniteQuery();
  const markRead = useMarkNotificationReadCommand();
  const markAllRead = useMarkAllNotificationsReadCommand();
  const navigate = useNavigate();
  const notifications = query.data?.pages.flatMap((page) => page.items) ?? [];

  const selectNotification = (notification: NotificationItem) => {
    if (!notification.isRead) {
      markRead.mutate(notification.id, { onError: (error) => toastApiError(error, t('markReadFailed')) });
    }
    if (notification.deepLink) navigate(notification.deepLink);
    onNotificationSelected?.();
  };

  if (query.isPending) return <NotificationsSkeleton />;
  if (query.isError) {
    return <div className="py-8 text-center text-[13px] text-[var(--text-2)]">{t('error')}</div>;
  }
  if (notifications.length === 0) {
    return <EmptyState title={t('empty')} />;
  }

  return (
    <div className="flex flex-col">
      {notifications.map((notification) => {
        const relative = getNotificationRelativeTime(new Date(notification.createdAt));
        const tone = typeToTone(notification.type);
        const Icon = notification.type === 'InvoicePaid' ? ReceiptText
          : notification.type === 'ReminderFailed' ? CircleAlert
            : notification.type === 'AppointmentCancelled' ? CircleAlert
              : notification.type === 'AppointmentRequested' ? CalendarClock
                : notification.type === 'SystemNotice' ? Bell : CircleCheck;
        return (
          <button
            key={notification.id}
            type="button"
            onClick={() => selectNotification(notification)}
            className="flex w-full gap-3 border-b border-[var(--border)] py-3 text-left transition-colors hover:bg-[var(--surface-2)]"
          >
            <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] ${toneClass(tone)}`}>
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-start gap-2">
                <span className="min-w-0 flex-1 truncate text-[14px] font-bold text-[var(--text)]">{notification.title}</span>
                {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--primary)]" aria-label={t('unread')} />}
              </span>
              <span className="mt-0.5 block line-clamp-2 text-[12.5px] leading-snug text-[var(--text-2)]">{notification.body}</span>
              <span className="mt-1 block text-[11.5px] font-medium text-[var(--text-3)]">{t(relative.key, { count: relative.count })}</span>
            </span>
          </button>
        );
      })}
      {query.hasNextPage && (
        <Button variant="ghost" className="mt-4" onClick={() => void query.fetchNextPage()} isLoading={query.isFetchingNextPage}>
          {t('loadMore')}
        </Button>
      )}
      {showMarkAll && (
        <Button
          variant="ghost"
          className="mt-4"
          onClick={() => markAllRead.mutate(undefined, { onError: (error) => toastApiError(error, t('markAllReadFailed')) })}
          isLoading={markAllRead.isPending}
        >
          {t('markAllRead')}
        </Button>
      )}
    </div>
  );
}

function toneClass(tone: ReturnType<typeof typeToTone>) {
  if (tone === 'success') return 'bg-[var(--primary-weak)] text-[var(--primary)]';
  if (tone === 'danger') return 'bg-[var(--danger-bg)] text-[var(--danger-solid)]';
  if (tone === 'warning') return 'bg-[var(--warning-bg)] text-[var(--warning-solid)]';
  if (tone === 'info') return 'bg-[var(--info-bg)] text-[var(--info-solid)]';
  return 'bg-[var(--surface-2)] text-[var(--text-3)]';
}
